import React, { FC, useState } from 'react';

import { MinusCircleOutlined, PlusCircleOutlined, TagsOutlined } from '@ant-design/icons';
import { Button, Flex, Popover, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { UserTagsSelect } from '@components/AntFields';

import { useApplyUserTagQuery, useRemoveUserTagQuery } from '@pages/Employees/hooks';

interface IProps {
  onRowsReset: () => void;
  selectedRows: React.Key[];
}

export const SelectedRowActions: FC<IProps> = ({ onRowsReset, selectedRows }) => {
  const assignmentsT = useTranslation(NSKeys.assignments);
  const [isTagsPopoverOpen, setIsTagsPopoverOpen] = useState(false);
  const [bulkTags, setBulkTags] = useState<string[]>([]);
  const { mutate: applyUserTag, isLoading: isApplyLoading } = useApplyUserTagQuery();
  const { mutate: removeUserTag, isLoading: isRemoveLoading } = useRemoveUserTagQuery();

  const handleApplyUserTag = () => {
    applyUserTag(
      { user_ids: selectedRows.map(Number), tag_uids: bulkTags },
      {
        onSuccess: () => {
          onRowsReset();
          setBulkTags([]);
        },
      }
    );
  };

  const handleRemoveUserTag = () => {
    removeUserTag(
      { user_ids: selectedRows.map(Number), tag_uids: bulkTags },
      {
        onSuccess: () => {
          onRowsReset();
          setBulkTags([]);
        },
      }
    );
  };

  return (
    <Flex vertical gap={8} style={{ marginBottom: 12 }}>
      <Space wrap>
        <Typography.Text strong>{assignmentsT.t('bulk.selectedCount', { count: selectedRows.length })}</Typography.Text>
      </Space>

      <Space wrap>
        <Popover
          placement="bottomLeft"
          arrow={false}
          trigger="click"
          open={isTagsPopoverOpen}
          onOpenChange={setIsTagsPopoverOpen}
          content={
            <Flex vertical gap={8} style={{ width: 320 }}>
              <UserTagsSelect
                removable
                style={{ width: '100%' }}
                value={bulkTags}
                onChange={setBulkTags}
                placeholder={assignmentsT.t('bulk.selectTag')}
              />
              <Typography.Text type="secondary">{assignmentsT.t('bulk.tagsFieldHint')}</Typography.Text>
            </Flex>
          }>
          <Button icon={<TagsOutlined />} shape="round" style={{ maxWidth: 360 }}>
            <Typography.Text type={bulkTags.length ? undefined : 'secondary'}>
              {assignmentsT.t('tags.fieldLabel')}
              {bulkTags.length ? `: ${bulkTags.length}` : ''}
            </Typography.Text>
          </Button>
        </Popover>

        {!!bulkTags.length && (
          <>
            <Button icon={<PlusCircleOutlined />} loading={isApplyLoading} onClick={handleApplyUserTag}>
              {assignmentsT.t('bulk.applyTag')}
            </Button>
            <Button danger icon={<MinusCircleOutlined />} loading={isRemoveLoading} onClick={handleRemoveUserTag}>
              {assignmentsT.t('bulk.removeTag')}
            </Button>
          </>
        )}
      </Space>
    </Flex>
  );
};
